import type { ReactNode } from "react";
import {
  Switch as AriaSwitch,
  type SwitchProps as AriaSwitchProps,
} from "react-aria-components";
import { cn } from "@core/utils";

interface SwitchProps extends Omit<AriaSwitchProps, "className" | "children"> {
  children?: ReactNode;
  /** Render the label sr-only — for rows where an adjacent text label already names it. */
  hideLabel?: boolean;
  className?: string;
}

/**
 * Toggle primitive (own-the-code) on React Aria's `Switch`. Used for the
 * round builder's boolean row controls ("Required", "Save to question bank").
 *
 * The track is a rendered element so its states come from tokens, and the knob translates on
 * the horizontal axis (`translate-x`).
 */
export function Switch({
  children,
  hideLabel = false,
  className,
  ...props
}: SwitchProps) {
  return (
    <AriaSwitch
      className={cn(
        // `relative` anchors React Aria's visually-hidden `<input>` to this control rather than
        // to some ancestor further up the page — see the note in Checkbox.tsx for why leaving it
        // out makes clicking the control scroll the page.
        "group relative flex cursor-pointer items-center gap-xs text-sm-medium text-(--text-secondary) outline-none",
        className
      )}
      {...props}
    >
      {({ isSelected, isFocusVisible, isDisabled }) => (
        <>
          <span
            className={cn(
              "flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors",
              isSelected ? "bg-action-primary" : "bg-gray-200",
              isDisabled && "opacity-50",
              isFocusVisible && "ring-2 ring-focus-ring ring-offset-2"
            )}
          >
            <span
              className={cn(
                "size-4 rounded-full bg-white shadow-sm transition-transform",
                isSelected && "translate-x-4"
              )}
            />
          </span>
          {children ? (
            <span className={cn(hideLabel && "sr-only")}>{children}</span>
          ) : null}
        </>
      )}
    </AriaSwitch>
  );
}

export type { SwitchProps };
